import React, { Fragment, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { getOverrides, useClasses } from '../../../utils/overrides';
import Popover from '../../utils/Popover';

import { createUseStyles } from '../../../utils/styles';
import styles from './styles';
const useStyles = createUseStyles(styles, 'Menus');

const SIZES = {
    small: 140,
    medium: 220,
    large: 300,
};

function Menus({
    title,
    icon,
    items,
    classes: classesProp,
    overrides: overridesProp,
    className: classNameProp,
    classNameMenu,
    triggerAction = 'click',
    size = 'large',
    placement = 'bottom-start',
    onVisibleChange,
}) {
    const [isOpen, setIsOpenState] = useState(false);
    const popupSize = SIZES[size];
    const classes = useClasses(useStyles, classesProp, { popupSize });

    const setIsOpen = useCallback(
        (visible) => {
            setIsOpenState(visible);
            onVisibleChange?.(visible);
        },
        [onVisibleChange],
    );

    const override = getOverrides(overridesProp, Menus.overrides);

    const handleItemClick = useCallback(
        (item) => {
            if (!item.infoItem) {
                if (item.onClick) {
                    item.onClick();
                }
                setIsOpen(false);
            }
        },
        [setIsOpen],
    );

    const rootClassName = useMemo(
        () => classnames(classes.root, classNameProp),
        [classes.root, classNameProp],
    );

    const menuClassName = useMemo(
        () => classnames(classes.menu, classNameMenu),
        [classes.menu, classNameMenu],
    );

    const menuContent = useMemo(
        () => (
            <div className={menuClassName} {...override.menu}>
                {items &&
                    items.map((item, id) => (
                        <Fragment key={id}>
                            {!item.divider && (
                                <div
                                    key={id}
                                    onClick={() => handleItemClick(item)}
                                    className={
                                        item.infoItem
                                            ? classnames(classes.menuItem, item.className)
                                            : classnames(
                                                  classes.menuItem,
                                                  item.className,
                                                  classes.clickable,
                                              )
                                    }
                                    {...override.item}
                                >
                                    {item.icon && (
                                        <span className={classes.itemIcon} {...override.itemIcon}>
                                            {item.icon}
                                        </span>
                                    )}
                                    <div
                                        className={classes.menuItemContent}
                                        {...override.menuItemContent}
                                    >
                                        {item.title}
                                        {item.description}
                                    </div>
                                </div>
                            )}
                            {item.divider && (
                                <div className={classes.divider} {...override.divider} />
                            )}
                        </Fragment>
                    ))}
            </div>
        ),
        [menuClassName, items, handleItemClick, classes, override],
    );

    const triggerArray = useMemo(() => [triggerAction], [triggerAction]);

    const popoverProps = useMemo(
        () => ({
            content: menuContent,
            trigger: triggerArray,
            placement,
            visible: isOpen,
            onVisibleChange: setIsOpen,
            overrides: {
                root: { className: classes.popoverRoot },
                content: { className: classes.popoverContent },
            },
        }),
        [menuContent, triggerArray, placement, isOpen, setIsOpen, classes],
    );

    return (
        <div className={rootClassName} {...override.root}>
            <Popover {...popoverProps}>
                <div
                    className={classes.trigger}
                    {...override.trigger}
                    tabIndex={0}
                    role="button"
                    aria-haspopup="menu"
                    aria-expanded={isOpen}
                >
                    {icon && icon}
                    {title && title}
                </div>
            </Popover>
        </div>
    );
}

Menus.overrides = ['root', 'trigger', 'menu', 'item', 'itemIcon', 'menuItemContent', 'divider'];

Menus.propTypes = {
    /** Content to display in the menu trigger button/element */
    title: PropTypes.any,
    /** Icon to display in the menu trigger button/element */
    icon: PropTypes.object,
    /** Custom CSS class for styling the main container */
    className: PropTypes.string,
    /** Custom CSS class for styling the dropdown menu container */
    classNameMenu: PropTypes.string,
    /**
     * Object with custom style overrides for inner elements.
     * You can override styles for 'root', 'trigger', 'menu', 'item', 'itemIcon', 'menuItemContent', and 'divider'.
     */
    overrides: PropTypes.object,
    /**
     * Array of items to display in the dropdown menu.
     * Each item can have a title, icon, onClick handler, and infoItem flag.
     * Items with the divider property set to true will render a separator line.
     */
    items: PropTypes.arrayOf(
        PropTypes.shape({
            /** Content to display as the item text */
            title: PropTypes.any,
            /** Additional description text to display under the title */
            description: PropTypes.any,
            /** Icon to display next to the item text */
            icon: PropTypes.object,
            /** When true, indicates this is an informational item that is not clickable */
            infoItem: PropTypes.bool,
            /** Function called when the item is clicked */
            onClick: PropTypes.func,
            /** When true, renders a horizontal divider instead of a menu item */
            divider: PropTypes.bool,
            /** Custom CSS class for styling this specific menu item */
            className: PropTypes.string,
        }),
    ),
    /**
     * Action that triggers the dropdown menu to open.
     * 'click': Opens on click (default)
     * 'hover': Opens on hover
     */
    triggerAction: PropTypes.oneOf(['hover', 'click']),
    /**
     * Size of the dropdown menu width.
     * 'small': 140px
     * 'medium': 220px
     * 'large': 300px (default)
     */
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    /**
     * Position of the dropdown menu relative to the trigger element.
     * Supports all floating-ui placement options.
     */
    placement: PropTypes.oneOf([
        'top',
        'top-start',
        'top-end',
        'right',
        'right-start',
        'right-end',
        'bottom',
        'bottom-start',
        'bottom-end',
        'left',
        'left-start',
        'left-end',
    ]),
    /**
     * Callback fired when the visibility of the menu changes.
     * Receives the new visibility state (boolean) as a parameter.
     */
    onVisibleChange: PropTypes.func,
};

export default React.memo(Menus);
